import { useState, useRef, useEffect } from "react";
import { Icon } from "@vds/icons";
import { Modal, ModalBody, ModalFooter } from "@vds/modals";
import { Body, Title } from "@vds/typography";
import axiosInstance from "../../global/axiosInterceptor";
import { updateMdnNotificationUsersEndPoint } from "../../configuration";
import { useDispatch, useSelector } from "react-redux";
import { showNotificationBanner } from "../../store/actions";
import { OTP_RESEND_ERROR, OTP_DIGIT_REGEX, NON_DIGIT_REGEX } from "../../global/constants/otpConstants";
import {
  OTP_MODAL_TITLE,
  OTP_MODAL_MESSAGE_PREFIX,
  OTP_MODAL_MESSAGE_SUFFIX,
  OTP_MODAL_ERROR_MESSAGE,
  OTP_MODAL_RESENDING,
  OTP_MODAL_RESEND_CODE,
  OTP_MODAL_VERIFYING,
  OTP_MODAL_SUBMIT,
  MODAL_CANCEL_BUTTON
} from "../../global/constants/userDetailsConstants";

import "./OTPVerificationModal.css";

const OTPVerificationModal = ({ 
  opened, 
  onClose, 
  onVerify, 
  isLoading,
  userEmail,
  pendingMDNData
}) => {
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpError, setOTPError] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (opened) {
      // Clear OTP values when modal opens to ensure fresh state
      setOtpValues(["", "", "", "", "", ""]);
      setOTPError(false);
      setResendCooldown(0);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [opened]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleOTPChange = (index, value) => {
  if (!OTP_DIGIT_REGEX.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(-1);
    setOtpValues(newOtpValues);
    setOTPError(false);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(NON_DIGIT_REGEX, "").slice(0, 6);
    const newOtpValues = [...otpValues];
    
    for (let i = 0; i < pastedData.length; i++) {
      newOtpValues[i] = pastedData[i];
    }
    
    setOtpValues(newOtpValues);
    setOTPError(false);
    
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async () => {
    const otpValue = otpValues.join("");
    if (otpValue.length < 6) {
      setOTPError(true);
      return;
    }
    
    try {
      await onVerify(otpValue);
    } catch (error) {
      setOTPError(true);
      setOtpValues(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || !pendingMDNData) return;

    setResendLoading(true);
    try {
      const mdnPayload = {
        mdn: pendingMDNData.mdn,
      };

      const response = await axiosInstance.post(
        updateMdnNotificationUsersEndPoint(),
        mdnPayload
      );

      if (response.status === 200 || response.status === 204) {
        setOtpValues(["", "", "", "", "", ""]);
        setOTPError(false);
        setResendCooldown(60);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      const notificationInfo = {
        type: "Resend Invite",
        priority: "critical",
        data: {
          subTitle: OTP_RESEND_ERROR,
        },
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
      setOTPError(true);
    } finally {
      setResendLoading(false);
    }
  };

  const handleClose = () => {
    setOtpValues(["", "", "", "", "", ""]);
    setOTPError(false);
    setResendCooldown(0);
    onClose();
  };

  const otpComplete = otpValues.every(val => val !== "");

  const formatPhoneWithLastFourDigits = (mdn) => {
    if (!mdn) return "";
    const digits = mdn.replace(/\D/g, "");
    const lastFour = digits.slice(-4);
    return `***-***-${lastFour}`;
  };

  const maskedPhoneNumber = formatPhoneWithLastFourDigits(pendingMDNData?.mdn);

  return (
    <Modal
      opened={opened}
      disableOutsideClick={true}
      hideCloseButton={false}
      className="otp-modal"
      onClose={handleClose}
    >
      <Title size="large" bold primitive="h2" viewport="desktop">
        {OTP_MODAL_TITLE}
      </Title>
      <ModalBody>
        <div className="otp-modal__content">
          <Body size="medium" viewport="desktop" primitive="p">
            <p>
              {OTP_MODAL_MESSAGE_PREFIX} {maskedPhoneNumber && <strong>{maskedPhoneNumber}</strong>} {OTP_MODAL_MESSAGE_SUFFIX}
            </p>          
            <div className="otp-modal__input-container">
              {otpValues.map((value, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={value}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className={`otp-modal__input ${otpError ? 'otp-modal__input--error' : ''}`}
                  data-testid={`otp_input_${index}`}
                />
              ))}
            </div>

            {otpError && (
              <p className="otp-modal__error-text">
                {OTP_MODAL_ERROR_MESSAGE}
              </p>
            )}

            <div className="otp-modal__resend-container">
              <button
                onClick={handleResendCode}
                disabled={resendLoading || resendCooldown > 0}
                className={`otp-modal__resend-button ${resendCooldown > 0 ? 'otp-modal__resend-button--disabled' : ''}`}
                data-testid="otp_resendCodeButton"
              >
                {resendLoading ? OTP_MODAL_RESENDING : resendCooldown > 0 ? `${OTP_MODAL_RESEND_CODE} (${resendCooldown}s)` : OTP_MODAL_RESEND_CODE}
              </button>
            </div>
          </Body>
        </div>
      </ModalBody>
      <ModalFooter
        buttonData={{
          primary: {
            "data-testid": "otp_submitButton",
            children: isLoading ? OTP_MODAL_VERIFYING : OTP_MODAL_SUBMIT,
            onClick: handleSubmit,
            disabled: isLoading || !otpComplete,
          },
          close: {
            "data-testid": "otp_cancelButton",
            children: MODAL_CANCEL_BUTTON,
            onClick: handleClose,
          },
        }}
      />
    </Modal>
  );
};

export default OTPVerificationModal;